import { useState } from 'react';
import { useBabies } from '../../../hooks/useBabies';

interface AddBabyModalProps {
  onClose: () => void;
}

export default function AddBabyModal({ onClose }: AddBabyModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    birth_date: '',
    gender: 'male' as 'male' | 'female',
    birth_weight: '',
    birth_height: '',
    photo_url: '',
    theme: 'day' as 'day' | 'night'
  });
  const [loading, setLoading] = useState(false);
  const [photoPreview, setPhotoPreview] = useState<string>('');
  const { addBaby } = useBabies();

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        setPhotoPreview(result);
        setFormData(prev => ({ ...prev, photo_url: result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const babyData = {
        name: formData.name,
        birth_date: formData.birth_date,
        gender: formData.gender,
        birth_weight: formData.birth_weight ? parseFloat(formData.birth_weight) : null,
        birth_height: formData.birth_height ? parseFloat(formData.birth_height) : null,
        photo_url: formData.photo_url || null,
        theme: formData.theme
      };

      const { error } = await addBaby(babyData);
      
      if (error) {
        console.error('Error adding baby:', error);
        return;
      }

      // Cerrar el modal
      onClose();
      
      // Refrescar la página después de un breve delay para que se vea el proceso
      setTimeout(() => {
        window.location.reload();
      }, 300);
      
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-800">Agregar bebé</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
          >
            <i className="ri-close-line text-gray-500"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Foto del bebé */}
          <div className="text-center">
            <div className="w-24 h-24 mx-auto mb-3 rounded-full overflow-hidden bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center">
              {photoPreview ? (
                <img src={photoPreview} alt="Preview" className="w-full h-full object-cover" />
              ) : (
                <i className="ri-camera-line text-gray-400 text-2xl"></i>
              )}
            </div>
            <label className="inline-block bg-blue-500 text-white px-4 py-2 rounded-xl cursor-pointer hover:bg-blue-600 transition-colors text-sm">
              <i className="ri-upload-line mr-2"></i>
              Subir foto
              <input
                type="file"
                accept="image/*"
                onChange={handlePhotoChange}
                className="hidden"
              />
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nombre *
            </label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Nombre del bebé"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Fecha de nacimiento *
            </label>
            <input
              type="date"
              required
              value={formData.birth_date}
              onChange={(e) => setFormData(prev => ({ ...prev, birth_date: e.target.value }))}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Género
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, gender: 'male' }))}
                className={`p-3 rounded-xl border-2 transition-colors ${
                  formData.gender === 'male'
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 text-gray-600'
                }`}
              >
                <i className="ri-mars-line text-xl mb-1"></i>
                <div className="text-sm font-medium">Niño</div>
              </button>
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, gender: 'female' }))}
                className={`p-3 rounded-xl border-2 transition-colors ${
                  formData.gender === 'female'
                    ? 'border-pink-500 bg-pink-50 text-pink-700'
                    : 'border-gray-200 text-gray-600'
                }`}
              >
                <i className="ri-venus-line text-xl mb-1"></i>
                <div className="text-sm font-medium">Niña</div>
              </button>
            </div>
          </div>

          {/* Tema día/noche */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tema preferido
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, theme: 'day' }))}
                className={`p-3 rounded-xl border-2 transition-colors ${
                  formData.theme === 'day'
                    ? 'border-yellow-500 bg-yellow-50 text-yellow-700'
                    : 'border-gray-200 text-gray-600'
                }`}
              >
                <i className="ri-sun-line text-xl mb-1"></i>
                <div className="text-sm font-medium">Día</div>
              </button>
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, theme: 'night' }))}
                className={`p-3 rounded-xl border-2 transition-colors ${
                  formData.theme === 'night'
                    ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                    : 'border-gray-200 text-gray-600'
                }`}
              >
                <i className="ri-moon-line text-xl mb-1"></i>
                <div className="text-sm font-medium">Noche</div>
              </button>
            </div>
          </div>

          {/* Birth Measurements */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <i className="ri-ruler-line text-blue-500"></i>
              Medidas al nacer
            </h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Peso (kg)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.birth_weight}
                  onChange={(e) => setFormData(prev => ({ ...prev, birth_weight: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="0.00"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Altura (cm)
                </label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  value={formData.birth_height}
                  onChange={(e) => setFormData(prev => ({ ...prev, birth_height: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="0.0"
                />
              </div>
            </div>
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-4 py-3 rounded-xl font-medium hover:from-blue-600 hover:to-indigo-700 transition-colors disabled:opacity-50"
            >
              {loading ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
